import { Plus, Upload } from "lucide-react";
import { useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCategories, useReorderCategoriesInJobType } from "@/hooks/use-categories";
import { useJobTypes } from "@/hooks/use-job-types";
import { BulkImportDialog } from "./BulkImportDialog";
import { CategoryCoverageWarning } from "./CategoryCoverageWarning";
import { CategoryRow } from "./CategoryRow";
import { NewCategoryDialog } from "./NewCategoryDialog";

type CategoryDialogState = { type: "none" } | { type: "create" } | { type: "import" };

export function CategoryLibrary() {
  const [filterCode, setFilterCode] = useState<string>("all");
  const [editMode, setEditMode] = useState(false);
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [dialog, setDialog] = useState<CategoryDialogState>({ type: "none" });

  const { data: jobTypes = [] } = useJobTypes();
  const {
    data: categories = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useCategories({
    jobTypeCode: filterCode === "all" ? undefined : filterCode,
    search: search || undefined,
    includeInactive,
  });

  const reorder = useReorderCategoriesInJobType();

  function closeDialog() {
    setDialog({ type: "none" });
  }

  function getJobTypeId(code: string): string | undefined {
    return jobTypes.find((jt) => jt.code === code)?.id;
  }

  function moveUp(index: number) {
    if (filterCode === "all" || index === 0) return;
    const jobTypeId = getJobTypeId(filterCode);
    if (!jobTypeId) return;
    const ids = categories.map((c) => c.id);
    const prev = ids[index - 1];
    const curr = ids[index];
    if (!prev || !curr) return;
    const newOrder = [...ids];
    newOrder[index - 1] = curr;
    newOrder[index] = prev;
    reorder.mutate({ jobTypeId, orderedCategoryIds: newOrder });
  }

  function moveDown(index: number) {
    if (filterCode === "all" || index >= categories.length - 1) return;
    const jobTypeId = getJobTypeId(filterCode);
    if (!jobTypeId) return;
    const ids = categories.map((c) => c.id);
    const next = ids[index + 1];
    const curr = ids[index];
    if (!next || !curr) return;
    const newOrder = [...ids];
    newOrder[index + 1] = curr;
    newOrder[index] = next;
    reorder.mutate({ jobTypeId, orderedCategoryIds: newOrder });
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load categories"}
        onRetry={() => refetch()}
      />
    );
  }

  const canReorder = filterCode !== "all" && editMode;

  const tableHeaders = [
    { label: "Category" },
    { label: "Job Types", className: "w-48" },
    { label: "Subtasks", className: "w-32" },
    { label: "Status", className: "w-28" },
    ...(editMode ? [{ label: "Actions", className: "w-40 text-right" }] : []),
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="h-section">Activity Categories</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage categories and subtasks used to classify time entries.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={editMode ? "default" : "outline"}
            size="sm"
            onClick={() => setEditMode((v) => !v)}
          >
            {editMode ? "Done" : "Reorder"}
          </Button>
          {editMode && (
            <Button variant="outline" size="sm" onClick={() => setDialog({ type: "import" })}>
              <Upload className="mr-2 h-4 w-4" />
              Import CSV
            </Button>
          )}
          <Button size="sm" onClick={() => setDialog({ type: "create" })}>
            <Plus className="mr-2 h-4 w-4" />
            New Category
          </Button>
        </div>
      </div>

      {/* Coverage warning */}
      <CategoryCoverageWarning />

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setFilterCode("all")}
          className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
            filterCode === "all"
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground hover:bg-muted/80"
          }`}
        >
          All
        </button>
        {jobTypes.map((jt) => (
          <button
            key={jt.id}
            type="button"
            onClick={() => setFilterCode(jt.code)}
            className={`chip-jobtype chip-jobtype--${jt.code} cursor-pointer transition-opacity ${
              filterCode === jt.code ? "opacity-100" : "opacity-60 hover:opacity-100"
            }`}
          >
            {jt.code}
          </button>
        ))}
      </div>

      {/* Search + inactive toggle */}
      <div className="flex items-center gap-3">
        <Input
          placeholder="Search categories…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="h-4 w-4 rounded border border-input"
          />
          Show inactive
        </label>
      </div>

      {/* Table */}
      <DataTable
        headers={tableHeaders}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Plus}
            title="No categories"
            description={
              search
                ? "No categories match your search."
                : filterCode === "all"
                  ? "Create your first activity category to get started."
                  : `No categories assigned to ${filterCode} yet.`
            }
          />
        }
      >
        {categories.map((category, index) => (
          <CategoryRow
            key={category.id}
            category={category}
            editMode={editMode}
            canMoveUp={canReorder && index > 0}
            canMoveDown={canReorder && index < categories.length - 1}
            isReordering={reorder.isPending}
            onMoveUp={() => moveUp(index)}
            onMoveDown={() => moveDown(index)}
          />
        ))}
      </DataTable>

      {/* Dialogs */}
      <NewCategoryDialog
        open={dialog.type === "create"}
        onOpenChange={(open) => !open && closeDialog()}
      />

      <BulkImportDialog
        open={dialog.type === "import"}
        onOpenChange={(open) => !open && closeDialog()}
      />
    </div>
  );
}
